import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Form } from 'reactstrap';
import { change, reduxForm } from 'redux-form';

import {
  clearMaintenanceBasicActivities,
  getMaintenanceBasicActivities,
} from '../../../../actions';

import Loader from '../../../common/Loader';
import FormFooter from '../../../common/forms/FormFooter';
import Alert from '../../../common/informative/Alert';
import DynamicForm from '../../../common/forms/DynamicForm';
import Modal from '../../../common/modal/Modal';

import { DATE_TIME_LOCAL } from '../../../../config/locale';
import { tzNormalizeDate } from '../../../../utils/date';
import { DEFAULT_QUERY_GET_ALL } from '../../../../config/queries';
import { MAINTENANCE_BASIC_CHECKLIST_COLUMNS } from '../../../../config/dynamicFormFields';

export const MAINTENANCE_CHECKLIST_BASIC_ACTIVITY_FORM_NAME =
  'MaintenanceChecklistBasicActivityForm';

const MAX_EXECUTION_HOURS_WITHOUT_WARNING = 72;

const toJSArray = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  if (Immutable.List.isList(value)) return value.toJS();

  return [];
};

const toPlainObject = (value) => {
  if (!value) return {};

  if (Immutable.Map.isMap(value)) {
    return value.toJS();
  }

  if (typeof value === 'object') {
    return value;
  }

  return {};
};

const getContentList = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  if (Immutable.List.isList(value)) return value.toJS();

  if (Immutable.Map.isMap(value)) {
    return toJSArray(value.get('content'));
  }

  if (value.content) return value.content;

  return [];
};

const getId = (value) => {
  if (!value) return null;

  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }

  if (Immutable.Map.isMap(value)) {
    return value.get('value') || value.get('id') || null;
  }

  return value.value || value.id || null;
};

const parseDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalizedValue =
    typeof value === 'string' ? value.replace(' ', 'T') : value;

  const date = new Date(normalizedValue);

  return Number.isNaN(date.getTime()) ? null : date;
};

const getExecutionHours = (startDate, endDate) => {
  const parsedStartDate = parseDateValue(startDate);
  const parsedEndDate = parseDateValue(endDate);

  if (!parsedStartDate || !parsedEndDate) {
    return 0;
  }

  return (
    (parsedEndDate.getTime() - parsedStartDate.getTime()) / (1000 * 60 * 60)
  );
};

const formatDateTime = (value) => {
  const date = parseDateValue(value);

  if (!date) return '-';

  return date.toLocaleString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatExecutionDuration = (executionHours) => {
  const totalMinutes = Math.floor(executionHours * 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [];

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'día' : 'días'}`);
  }

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`);
  }

  return parts.length > 0 ? parts.join(', ') : '0 horas';
};

const getBasicActivityLabel = (basicActivity, index) => {
  const activity = toPlainObject(basicActivity);

  return (
    activity.organizationChart ||
    activity.description ||
    activity.activityName ||
    `Actividad básica ${index + 1}`
  );
};

const getLongExecutionWarnings = (basicChecklist) =>
  toJSArray(basicChecklist).reduce((warnings, basicActivityValue, index) => {
    const basicActivity = toPlainObject(basicActivityValue);

    const executionHours = getExecutionHours(
      basicActivity.startDate,
      basicActivity.endDate,
    );

    if (executionHours <= MAX_EXECUTION_HOURS_WITHOUT_WARNING) {
      return warnings;
    }

    warnings.push({
      key:
        getId(basicActivity.maintenanceBasicActivityId) ||
        getId(basicActivity.activityId) ||
        index,
      activityLabel: getBasicActivityLabel(basicActivity, index),
      startDate: basicActivity.startDate,
      endDate: basicActivity.endDate,
      executionHours,
    });

    return warnings;
  }, []);

const buildBasicChecklistFromActivities = (activities) => {
  const currentDate = new Date();

  const startDate = tzNormalizeDate({
    date: currentDate,
    format: DATE_TIME_LOCAL,
  });

  const endDateValue = new Date(currentDate);
  endDateValue.setHours(endDateValue.getHours() + 1);

  const endDate = tzNormalizeDate({
    date: endDateValue,
    format: DATE_TIME_LOCAL,
  });

  return activities.map((activity) => ({
    maintenanceBasicActivityId: activity.id,
    activityId: activity.activityId,
    maintenanceFailModeId: activity.maintenanceFailModeId,
    organizationChart: activity.description,
    startDate,
    endDate,
    employee: null,
    observations: '',
  }));
};

const buildEmployeeList = (basicActivityValue) => {
  const basicActivity = toPlainObject(basicActivityValue);

  if (basicActivity.maintenancePlanningActivityExecutionEmployeeList) {
    return basicActivity.maintenancePlanningActivityExecutionEmployeeList;
  }

  const employees = toJSArray(
    basicActivity.employee || basicActivity.employeeId,
  ).filter(Boolean);

  return employees
    .map((employee) => ({
      employeeId: getId(employee),
    }))
    .filter((employee) => employee.employeeId);
};

const buildBasicActivityExecutionPayload = (basicActivityValue) => {
  const basicActivity = toPlainObject(basicActivityValue);

  return {
    maintenanceBasicActivityId: getId(basicActivity.maintenanceBasicActivityId),
    activityId: getId(basicActivity.activityId),
    maintenanceFailModeId: getId(basicActivity.maintenanceFailModeId),
    startDate: basicActivity.startDate,
    endDate: basicActivity.endDate,
    comment: basicActivity.comment || basicActivity.observations || '',
    maintenancePlanningActivityExecutionEmployeeList:
      buildEmployeeList(basicActivity),
  };
};

export const MaintenanceChecklistBasicActivityForm = ({
  handleSubmit,
  onSubmit,
  loading,
  basicActivities,
  loadingBasicActivities,
  dispatchChange,
  dispatchGetMaintenanceBasicActivities,
  dispatchClearMaintenanceBasicActivities,
  showFooter,
}) => {
  const [showExecutionWarning, setShowExecutionWarning] = useState(false);

  const [executionWarnings, setExecutionWarnings] = useState([]);

  const [pendingSubmitPayload, setPendingSubmitPayload] = useState(null);

  const basicActivitiesList = useMemo(
    () => getContentList(basicActivities),
    [basicActivities],
  );

  useEffect(() => {
    dispatchGetMaintenanceBasicActivities(DEFAULT_QUERY_GET_ALL);

    return () => {
      dispatchClearMaintenanceBasicActivities();

      dispatchChange(
        MAINTENANCE_CHECKLIST_BASIC_ACTIVITY_FORM_NAME,
        'basicChecklist',
        [],
      );
    };
  }, []);

  useEffect(() => {
    if (basicActivitiesList.length === 0) {
      dispatchChange(
        MAINTENANCE_CHECKLIST_BASIC_ACTIVITY_FORM_NAME,
        'basicChecklist',
        [],
      );

      return;
    }

    dispatchChange(
      MAINTENANCE_CHECKLIST_BASIC_ACTIVITY_FORM_NAME,
      'basicChecklist',
      buildBasicChecklistFromActivities(basicActivitiesList),
    );
  }, [basicActivitiesList]);

  const closeExecutionWarning = () => {
    setShowExecutionWarning(false);
    setExecutionWarnings([]);
    setPendingSubmitPayload(null);
  };

  const continueExecutionSubmit = async () => {
    const payload = pendingSubmitPayload;

    closeExecutionWarning();

    if (payload) {
      await onSubmit(payload);
    }
  };

  const onHandleSubmit = (formValues) => {
    const basicChecklist = toJSArray(formValues.basicChecklist);

    const maintenanceChecklistBasicActivityExecutionList = basicChecklist.map(
      buildBasicActivityExecutionPayload,
    );

    const warnings = getLongExecutionWarnings(basicChecklist);

    if (warnings.length > 0) {
      setPendingSubmitPayload(maintenanceChecklistBasicActivityExecutionList);

      setExecutionWarnings(warnings);
      setShowExecutionWarning(true);

      return;
    }

    onSubmit(maintenanceChecklistBasicActivityExecutionList);
  };

  if (loading || loadingBasicActivities) {
    return <Loader />;
  }

  if (basicActivitiesList.length === 0) {
    return (
      <Alert type="info" message="No hay actividades básicas configuradas." />
    );
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onHandleSubmit)}>
        <DynamicForm
          name="basicChecklist"
          columns={MAINTENANCE_BASIC_CHECKLIST_COLUMNS}
          showRemoveButton={false}
          showAddButton={false}
        />

        {showFooter && (
          <FormFooter
            saveButtonDisabled={loading}
            saveButtonIcon={loading ? 'fa fa-spinner fa-spin' : 'fa fa-save'}
          />
        )}
      </Form>

      <Modal
        show={showExecutionWarning}
        title="Advertencia de cierre"
        size="lg"
        body={
          <div>
            <p>
              Está cerrando actividades básicas con más de 3 días de ejecución.
              Revise el detalle antes de continuar.
            </p>

            <p>
              <strong>
                Ejecuciones encontradas: {executionWarnings.length}
              </strong>
            </p>

            {executionWarnings.map((warning, index) => (
              <div key={warning.key} className="border rounded p-3 mb-3">
                <p className="mb-2">
                  <strong>
                    {index + 1}. {warning.activityLabel}
                  </strong>
                </p>

                <p className="mb-1">
                  <strong>Tipo:</strong> Actividad básica
                </p>

                <p className="mb-1">
                  <strong>Fecha/Hora Inicio:</strong>{' '}
                  {formatDateTime(warning.startDate)}
                </p>

                <p className="mb-1">
                  <strong>Fecha/Hora Final:</strong>{' '}
                  {formatDateTime(warning.endDate)}
                </p>

                <p className="mb-0">
                  <strong>Tiempo de ejecución:</strong>{' '}
                  {formatExecutionDuration(warning.executionHours)}
                </p>
              </div>
            ))}

            <p className="mb-0">¿Desea cerrar estas actividades y continuar?</p>
          </div>
        }
        onClickClose={closeExecutionWarning}
        onClickCancel={closeExecutionWarning}
        onClickConfirm={continueExecutionSubmit}
        cancelButtonText="Cerrar"
        confirmButtonText="Continuar"
      />
    </>
  );
};

MaintenanceChecklistBasicActivityForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  basicActivities: PropTypes.oneOfType([
    PropTypes.instanceOf(Immutable.Map),
    PropTypes.instanceOf(Immutable.List),
    PropTypes.array,
    PropTypes.object,
  ]),
  loadingBasicActivities: PropTypes.bool.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  dispatchGetMaintenanceBasicActivities: PropTypes.func.isRequired,
  dispatchClearMaintenanceBasicActivities: PropTypes.func.isRequired,
  showFooter: PropTypes.bool,
};

MaintenanceChecklistBasicActivityForm.defaultProps = {
  loading: false,
  basicActivities: Immutable.List(),
  showFooter: true,
};

const mapStateToProps = ({ MechanicalMaintenanceUnit }) => ({
  basicActivities: MechanicalMaintenanceUnit.MaintenanceChecklist.getIn([
    'basicActivities',
    'content',
  ]),

  loadingBasicActivities: MechanicalMaintenanceUnit.MaintenanceChecklist.getIn([
    'basicActivities',
    'loading',
  ]),

  loading: !MechanicalMaintenanceUnit.MaintenanceChecklist.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

const mapDispatchToProps = {
  dispatchChange: change,
  dispatchGetMaintenanceBasicActivities: getMaintenanceBasicActivities,
  dispatchClearMaintenanceBasicActivities: clearMaintenanceBasicActivities,
};

const formComponent = reduxForm({
  form: MAINTENANCE_CHECKLIST_BASIC_ACTIVITY_FORM_NAME,
})(MaintenanceChecklistBasicActivityForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
